import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addTradeFormEvent, isTradeEventEligible } from "../api/trade";
import { isUserAuthorizedByServer } from "../api/userAccess";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import GiveawayRequestForm from "../components/GiveawayRequestForm";
import PageLoading from "../components/PageLoading";
import ShiftTradeForm from "../components/ShiftTradeForm";
import TimeTradeForm from "../components/TimeTradeForm";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import { selectStationFilter, selectUserSession } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { BREADCRUMBS_TITLE, CANCEL_WARNING_MESSAGE_ACTION_TYPE, HOME_PATH, STATUS_OFFERED } from "../util/constants.js";
import {
  validateDuplicateShifts,
  validateEmployee,
  validateReliefShift,
  validateShiftEndDate,
  validateShiftEndTime,
  validateShiftStartDate,
  validateShiftStartTime,
  validateShiftWorkgroup,
  validateStation,
  validateTradeFormType,
} from "../util/inputValidation.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THE_REQUEST_MESSAGE,
  REFERENCE_NUMBER_SUB_TITLE,
  REQUEST_TRADE_TITLE,
  TRADE_CONFIRMATION_TITLE,
  TRADE_ELIGIBILITY_ERROR_MESSAGE,
  TRADE_ELIGIBILITY_TITLE,
  TRADE_OFFERED_MESSAGE,
} from "../util/messageConstants";
import { buildCreateTradeFormEventRequestBody } from "../util/tradeFormEventBuilder.js";
import {
  getIsTradeBetweenAgentAndSup,
  getUnionContractGivenWorkgroup,
  getWorkgroupGivenJobCode,
  isManager,
} from "../util/workgroupMapper.js";
import { useRef } from "react";
import { getEmployeeStation, getEmployeeStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import { updateStationFilter } from "../slices/userSession/userSessionSlice";
import { isDSTDay } from "../util/dateHelper";

const EmployeeRequestTradePage = (props) => {
  const isAdminPage = false;
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const dispatch = useDispatch();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isTradeSuccessDialogOpen, setTradeSuccessDialog] = useState(false);
  const [isTradeEventEligibilityWarningDialogOpen, setIsTradeEventEligibilityWarningDialogOpen] = useState(false);
  const tradeEligibilityErrorMessage = useRef("");
  const [isTradeFormSubmissionTriggered, setIsTradeFormSubmissionTriggered] = useState(false);
  const [isTradeFormSubmissionLoading, setIsTradeFormSubmissionLoading] = useState(false);
  const [isTradeFormSubmissionError, setIsTradeFormSubmissionError] = useState(false);
  const [tradeFormSubmissionErrors, setTradeFormSubmissionErrors] = useState("");
  // Generic Trade Form State
  const [station, setStation] = useState("");
  const [stationList, setStationList] = useState([]);
  const [stationErrors, setStationErrors] = useState("");
  const [tradeFormType, setTradeFormType] = useState("");
  const [tradeFormTypeErrors, setTradeFormTypeErrors] = useState("");
  const [workgroup, setWorkgroup] = useState("");
  const [unionContract, setUnionContract] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");
  const [createdTrade, setCreatedTrade] = useState([]);
  // Specific Trade Form Type State
  const [offeredEmployeeId, setOfferedEmployeeId] = useState("");
  const [offeredEmployeeWorkgroup, setOfferedEmployeeWorkgroup] = useState("");
  const [offeredShiftStart, setOfferedShiftStart] = useState(null);
  const [offeredShiftStartErrors, setOfferedShiftStartErrors] = useState("");
  const [offeredShiftStartHour, setOfferedShiftStartHour] = useState("");
  const [offeredShiftStartMinute, setOfferedShiftStartMinute] = useState("");
  const [offeredShiftStartTimeErrors, setOfferedShiftStartTimeErrors] = useState("");
  const [offeredShiftStartHourTouched, setOfferedShiftStartHourTouched] = useState(false);
  const [offeredShiftStartMinuteTouched, setOfferedShiftStartMinuteTouched] = useState(false);
  const [offeredShiftEnd, setOfferedShiftEnd] = useState(null);
  const [offeredShiftEndErrors, setOfferedShiftEndErrors] = useState("");
  const [offeredShiftEndTouched, setOfferedShiftEndTouched] = useState(false);
  const [offeredShiftEndHour, setOfferedShiftEndHour] = useState("");
  const [offeredShiftEndMinute, setOfferedShiftEndMinute] = useState("");
  const [offeredShiftEndTimeErrors, setOfferedShiftEndTimeErrors] = useState("");
  const [offeredShiftEndHourTouched, setOfferedShiftEndHourTouched] = useState(false);
  const [offeredShiftEndMinuteTouched, setOfferedShiftEndMinuteTouched] = useState(false);
  const [isOfferedReliefShift, setIsOfferedReliefShift] = useState(false);
  const [offeredReliefShiftErrors, setOfferedReliefShiftErrors] = useState("");
  const [requestedEmployeeAutocompleteKey, setRequestedEmployeeAutocompleteKey] = useState("");
  const [requestedEmployeeId, setRequestedEmployeeId] = useState("");
  const [requestedEmployeeErrors, setRequestedEmployeeErrors] = useState("");
  const [requestedEmployeeFirstName, setRequestedEmployeeFirstName] = useState("");
  const [requestedEmployeeLastName, setRequestedEmployeeLastName] = useState("");
  const [requestedEmployeeWorkgroup, setRequestedEmployeeWorkgroup] = useState("");
  const [requestedShiftStart, setRequestedShiftStart] = useState(null);
  const [requestedShiftStartErrors, setRequestedShiftStartErrors] = useState("");
  const [requestedShiftStartHour, setRequestedShiftStartHour] = useState("");
  const [requestedShiftStartMinute, setRequestedShiftStartMinute] = useState("");
  const [requestedShiftStartTimeErrors, setRequestedShiftStartTimeErrors] = useState("");
  const [requestedShiftStartHourTouched, setRequestedShiftStartHourTouched] = useState(false);
  const [requestedShiftStartMinuteTouched, setRequestedShiftStartMinuteTouched] = useState(false);
  const [requestedShiftEnd, setRequestedShiftEnd] = useState(null);
  const [requestedShiftEndErrors, setRequestedShiftEndErrors] = useState("");
  const [requestedShiftEndTouched, setRequestedShiftEndTouched] = useState(false);
  const [requestedShiftEndHour, setRequestedShiftEndHour] = useState("");
  const [requestedShiftEndMinute, setRequestedShiftEndMinute] = useState("");
  const [requestedShiftEndTimeErrors, setRequestedShiftEndTimeErrors] = useState("");
  const [requestedShiftEndHourTouched, setRequestedShiftEndHourTouched] = useState(false);
  const [requestedShiftEndMinuteTouched, setRequestedShiftEndMinuteTouched] = useState(false);
  const [isRequestedReliefShift, setIsRequestedReliefShift] = useState(false);
  const [requestedReliefShiftErrors, setRequestedReliefShiftErrors] = useState("");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const [isOfferedShiftDSTWarningOpen, setIsOfferedShiftDSTWarningOpen] = useState(false);
  const [isRequestedShiftDSTWarningOpen, setIsRequestedShiftDSTWarningOpen] = useState(false);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const userWorkgroup = getWorkgroupGivenJobCode(userSession.jobCode);
      const userUnionContract = getUnionContractGivenWorkgroup(userWorkgroup);
      setIsPageLoading(false);
      setStation(getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter));
      setStationList(getEmployeeStationList(userSession));
      setOfferedEmployeeId(userSession.eid);
      setWorkgroup(userWorkgroup);
      setUnionContract(userUnionContract);
      setOfferedEmployeeWorkgroup(userWorkgroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(!isManager(userSession.jobCode));
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    if (offeredShiftStart && offeredShiftEnd) {
      if (isDSTDay(offeredShiftStart) || isDSTDay(offeredShiftEnd)) {
        setIsOfferedShiftDSTWarningOpen(true);
      } else {
        setIsOfferedShiftDSTWarningOpen(false);
      }
    }
  }, [offeredShiftStart, offeredShiftEnd]);

  useEffect(() => {
    if (requestedShiftStart && requestedShiftEnd) {
      if (isDSTDay(requestedShiftStart) || isDSTDay(requestedShiftEnd)) {
        setIsRequestedShiftDSTWarningOpen(true);
      } else {
        setIsRequestedShiftDSTWarningOpen(false);
      }
    }
  }, [requestedShiftStart, requestedShiftEnd]);

  const clearBottomForm = () => {
    setOfferedShiftStart(null);
    setOfferedShiftStartErrors("");
    setOfferedShiftStartHour("");
    setOfferedShiftStartMinute("");
    setOfferedShiftStartTimeErrors("");
    setOfferedShiftStartHourTouched(false);
    setOfferedShiftStartMinuteTouched(false);
    setOfferedShiftEnd(null);
    setOfferedShiftEndErrors("");
    setOfferedShiftEndTouched(false);
    setOfferedShiftEndHour("");
    setOfferedShiftEndMinute("");
    setOfferedShiftEndTimeErrors("");
    setOfferedShiftEndHourTouched(false);
    setOfferedShiftEndMinuteTouched(false);
    setIsOfferedReliefShift(false);
    setOfferedReliefShiftErrors("");
    setRequestedEmployeeId("");
    setRequestedEmployeeErrors("");
    setRequestedEmployeeFirstName("");
    setRequestedEmployeeLastName("");
    setRequestedEmployeeWorkgroup("");
    setRequestedShiftStart(null);
    setRequestedShiftStartErrors("");
    setRequestedShiftStartHour("");
    setRequestedShiftStartMinute("");
    setRequestedShiftStartTimeErrors("");
    setRequestedShiftStartHourTouched(false);
    setRequestedShiftStartMinuteTouched(false);
    setRequestedShiftEnd(null);
    setRequestedShiftEndErrors("");
    setRequestedShiftEndTouched(false);
    setRequestedShiftEndHour("");
    setRequestedShiftEndMinute("");
    setRequestedShiftEndTimeErrors("");
    setRequestedShiftEndHourTouched(false);
    setRequestedShiftEndMinuteTouched(false);
    setIsRequestedReliefShift(false);
    setRequestedReliefShiftErrors("");
    setTradeFormSubmissionErrors("");
    setIsTradeFormSubmissionError(false);
    setIsTradeFormSubmissionTriggered(false);
    setIsOfferedShiftDSTWarningOpen(false);
    setIsRequestedShiftDSTWarningOpen(false);
  };

  const setDuplicateShiftErrors = (error) => {
    setOfferedShiftStartErrors(error);
    setOfferedShiftStartTimeErrors(error === "" ? "" : " ");
    setOfferedShiftEndErrors(error);
    setOfferedShiftEndTimeErrors(error === "" ? "" : " ");
    setRequestedShiftStartErrors(error);
    setRequestedShiftStartTimeErrors(error === "" ? "" : " ");
    setRequestedShiftEndErrors(error);
    setRequestedShiftEndTimeErrors(error === "" ? "" : " ");
  };

  const handleStationChange = (event) => {
    clearBottomForm();
    dispatch(updateStationFilter(event.target.value));
    setStation(event.target.value);
    setRequestedEmployeeAutocompleteKey(`${event.target.value}_${workgroup}_${tradeFormType}`);
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
  };

  const handleTradeFormTypeSelectChange = (event) => {
    clearBottomForm();
    setTradeFormType(event.target.value);
    setTradeFormTypeErrors(validateTradeFormType(event.target.value));
    setRequestedEmployeeAutocompleteKey(`${station}_${workgroup}_${event.target.value}`);
  };

  const handleTradeFormTypeSelectBlur = (event) => {
    setTradeFormTypeErrors(validateTradeFormType(event.target.value));
  };

  const prepareNewTradeEventPayload = () => {
    let newTradeEventPayload = {
      isPageLoading: isPageLoading,
      station: station,
      workgroup: workgroup,
      isSupervisorInvolved: getIsTradeBetweenAgentAndSup(offeredEmployeeWorkgroup, requestedEmployeeWorkgroup),
      tradeFormType: tradeFormType,
      offeredEmployeeId: offeredEmployeeId,
      offeredEmployeeFirstName: userSession.firstName,
      offeredEmployeeLastName: userSession.lastName,
      offeredEmployeeWorkgroup: offeredEmployeeWorkgroup,
      offeredShiftStart: offeredShiftStart,
      offeredShiftStartHour: parseInt(offeredShiftStartHour).toString(),
      offeredShiftStartMinute: parseInt(offeredShiftStartMinute).toString(),
      offeredShiftEnd: offeredShiftEnd,
      offeredShiftEndHour: parseInt(offeredShiftEndHour).toString(),
      offeredShiftEndMinute: parseInt(offeredShiftEndMinute).toString(),
      isOfferedReliefShift: isOfferedReliefShift,
      requestedEmployeeId: requestedEmployeeId,
      requestedEmployeeFirstName: requestedEmployeeFirstName,
      requestedEmployeeLastName: requestedEmployeeLastName,
      requestedEmployeeWorkgroup: requestedEmployeeWorkgroup,
      requestedShiftStart: requestedShiftStart,
      requestedShiftStartHour: parseInt(requestedShiftStartHour).toString(),
      requestedShiftStartMinute: parseInt(requestedShiftStartMinute).toString(),
      requestedShiftEnd: requestedShiftEnd,
      requestedShiftEndHour: parseInt(requestedShiftEndHour).toString(),
      requestedShiftEndMinute: parseInt(requestedShiftEndMinute).toString(),
      isRequestedReliefShift: isRequestedReliefShift,
    };
    return newTradeEventPayload;
  };

  const handleSubmitClicked = async () => {
    if (tradeFormType === "Giveaway" && !isGiveawayFormValidationSuccess()) {
      setIsSubmitDialogOpen(false);
      return;
    }
    if (tradeFormType !== "Giveaway" && !isFormValidationSuccess()) {
      setIsSubmitDialogOpen(false);
      return;
    }

    let newTradeEventPayload = prepareNewTradeEventPayload();

    setIsTradeFormSubmissionError(false);
    setIsTradeFormSubmissionTriggered(true);
    setIsTradeFormSubmissionLoading(true);

    const isTradeEligible = await isTradeEventEligible(newTradeEventPayload);

    if (!isTradeEligible) {
      tradeEligibilityErrorMessage.current = TRADE_ELIGIBILITY_ERROR_MESSAGE;
      setIsTradeFormSubmissionTriggered(false);
      setIsTradeFormSubmissionLoading(false);
      setIsSubmitDialogOpen(false);
      setIsTradeEventEligibilityWarningDialogOpen(true);
      return;
    } else {
      tradeEligibilityErrorMessage.current = "";
    }

    let payload = JSON.stringify(
      buildCreateTradeFormEventRequestBody(STATUS_OFFERED, newTradeEventPayload, userSession)
    );

    addTradeFormEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        // console.log(result);
        if (result.error) {
          console.error("Error");
          setTradeFormSubmissionErrors(result.error);
          setIsTradeFormSubmissionError(true);
          setIsTradeFormSubmissionLoading(false);
          setIsSubmitDialogOpen(false);
        } else {
          if (result.newTrades.length > 0) {
            console.log("Success!");
            setCreatedTrade(result.newTrades);
            setIsSubmitDialogOpen(false);
            setTradeSuccessDialog(true);
            setIsTradeFormSubmissionLoading(false);
          }
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsTradeFormSubmissionLoading(false);
      });
  };

  // Specific Trade Form Type State Handlers - START
  const handleEmpOneSelectChange = (event) => {
    setOfferedEmployeeId(event.target.value);
  };

  const handleEmpTwoSelectChange = (event, newValue) => {
    var employeeId = "";
    var employeeFirstName = "";
    var employeeLastName = "";
    var employeeWorkgroup = "";

    if (newValue) {
      employeeId = newValue.PersonNumber;
      employeeFirstName = pullEmployeeFirstName(newValue.FullName);
      employeeLastName = pullEmployeeLastName(newValue.FullName);
      employeeWorkgroup = newValue.Workgroup;
    }
    setRequestedEmployeeId(employeeId);
    setRequestedEmployeeFirstName(employeeFirstName);
    setRequestedEmployeeLastName(employeeLastName);
    setRequestedEmployeeWorkgroup(employeeWorkgroup);
    setRequestedEmployeeErrors(
      validateEmployee(employeeId, employeeFirstName, employeeLastName, station, employeeWorkgroup)
    );
  };

  const pullEmployeeFirstName = (eligibleEmployeeEntry) => {
    return eligibleEmployeeEntry.split(",")[1].split(" ")[1];
  };

  const pullEmployeeLastName = (eligibleEmployeeEntry) => {
    return eligibleEmployeeEntry.split(",")[0];
  };

  const handleOfferedShiftStartChange = (date) => {
    setOfferedShiftStart(date);
    setOfferedShiftEnd(date);
    let offeredShiftStartErrors = validateShiftStartDate(tradeFormType, date, requestedShiftStart, station, false);
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndErrors = "";
    let offeredShiftEndTimeErrors = "";
    setOfferedShiftEndErrors(offeredShiftEndErrors);
    setOfferedShiftStartErrors(offeredShiftStartErrors);

    if (offeredShiftStartHourTouched && offeredShiftStartMinuteTouched) {
      const tradeStartEvent = {
        offeredShiftStart: { date: date, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
        requestedShiftStart: {
          date: requestedShiftStart,
          hour: requestedShiftStartHour,
          minute: requestedShiftStartMinute,
        },
      };
      offeredShiftStartTimeErrors = validateShiftStartTime(
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (offeredShiftEndTouched) {
      offeredShiftEndErrors = validateShiftEndDate(date, date, station);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
    }

    if (offeredShiftEndHourTouched && offeredShiftEndMinuteTouched) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        date,
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === ""
    ) {
      let duplicateShiftErrors = validateDuplicateShifts(
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        date,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftStartHourChange = (event) => {
    setOfferedShiftStartHour(event.target.value);
    setOfferedShiftStartHourTouched(true);
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndTimeErrors = "";
    let offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      false
    );
    setOfferedShiftStartErrors(offeredShiftStartErrors);

    if (offeredShiftStartMinuteTouched && offeredShiftStartErrors === "") {
      const tradeStartEvent = {
        offeredShiftStart: { date: offeredShiftStart, hour: event.target.value, minute: offeredShiftStartMinute },
        requestedShiftStart: {
          date: requestedShiftStart,
          hour: requestedShiftStartHour,
          minute: requestedShiftStartMinute,
        },
      };
      offeredShiftStartTimeErrors = validateShiftStartTime(
        offeredShiftStart,
        event.target.value,
        offeredShiftStartMinute,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (
      offeredShiftStartMinuteTouched &&
      offeredShiftEndHourTouched &&
      offeredShiftEndMinuteTouched &&
      offeredShiftStartErrors === ""
    ) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        event.target.value,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftStartTimeErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        event.target.value,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftStartMinuteChange = (event) => {
    setOfferedShiftStartMinute(event.target.value);
    setOfferedShiftStartMinuteTouched(true);
    let offeredShiftStartTimeErrors = "";
    let offeredShiftEndTimeErrors = "";
    let offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      false
    );
    setOfferedShiftStartErrors(offeredShiftStartErrors);

    if (offeredShiftStartHourTouched && offeredShiftStartErrors === "") {
      const tradeStartEvent = {
        offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: event.target.value },
        requestedShiftStart: {
          date: requestedShiftStart,
          hour: requestedShiftStartHour,
          minute: requestedShiftStartMinute,
        },
      };
      offeredShiftStartTimeErrors = validateShiftStartTime(
        offeredShiftStart,
        offeredShiftStartHour,
        event.target.value,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
    }

    if (
      offeredShiftStartHourTouched &&
      offeredShiftEndHourTouched &&
      offeredShiftEndMinuteTouched &&
      offeredShiftStartErrors === ""
    ) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        event.target.value,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftStartTimeErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        event.target.value,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndChange = (date) => {
    setOfferedShiftEnd(date);
    setOfferedShiftEndTouched(true);
    let offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      false
    );
    setOfferedShiftStartErrors(offeredShiftStartErrors);
    let offeredShiftEndErrors = validateShiftEndDate(offeredShiftStart, date, station);
    let offeredShiftEndTimeErrors = "";
    setOfferedShiftEndErrors(offeredShiftEndErrors);

    if (offeredShiftEndHourTouched && offeredShiftEndMinuteTouched && offeredShiftStartErrors === "") {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        date,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndErrors === "" && offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        date,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndHourChange = (event) => {
    setOfferedShiftEndHour(event.target.value);
    setOfferedShiftEndHourTouched(true);
    let offeredShiftEndTimeErrors = "";
    let offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      false
    );
    setOfferedShiftStartErrors(offeredShiftStartErrors);
    let offeredShiftEndErrors = validateShiftEndDate(offeredShiftStart, offeredShiftEnd, station);
    setOfferedShiftEndErrors(offeredShiftEndErrors);

    if (
      offeredShiftEndMinuteTouched &&
      offeredShiftStartHourTouched &&
      offeredShiftStartMinuteTouched &&
      offeredShiftEndErrors === "" &&
      offeredShiftStartErrors === ""
    ) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        event.target.value,
        offeredShiftEndMinute,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        event.target.value,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedShiftEndMinuteChange = (event) => {
    setOfferedShiftEndMinute(event.target.value);
    setOfferedShiftEndMinuteTouched(true);
    let offeredShiftEndTimeErrors = "";
    let offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      false
    );
    setOfferedShiftStartErrors(offeredShiftStartErrors);
    let offeredShiftEndErrors = validateShiftEndDate(offeredShiftStart, offeredShiftEnd, station);
    setOfferedShiftEndErrors(offeredShiftEndErrors);

    if (
      offeredShiftEndHourTouched &&
      offeredShiftStartHourTouched &&
      offeredShiftStartMinuteTouched &&
      offeredShiftEndErrors === "" &&
      offeredShiftStartErrors === ""
    ) {
      offeredShiftEndTimeErrors = validateShiftEndTime(
        offeredShiftStart,
        offeredShiftEnd,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEndHour,
        event.target.value,
        unionContract,
        station
      );
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
    }

    if (offeredShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        event.target.value,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleOfferedReliefShiftSelectChange = (event) => {
    setIsOfferedReliefShift(event.target.value);
    setOfferedReliefShiftErrors(validateReliefShift(event.target.value));
  };

  const handleRequestedShiftStartChange = (date) => {
    setRequestedShiftStart(date);
    setRequestedShiftEnd(date);
    let requestedShiftStartErrors = validateShiftStartDate(tradeFormType, date, offeredShiftStart, station, false);
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndErrors = "";
    let requestedShiftEndTimeErrors = "";
    setRequestedShiftStartErrors(requestedShiftStartErrors);

    if (requestedShiftStartHourTouched && requestedShiftStartMinuteTouched) {
      const tradeStartEvent = {
        offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
        requestedShiftStart: { date: date, hour: requestedShiftStartHour, minute: requestedShiftStartMinute },
      };
      requestedShiftStartTimeErrors = validateShiftStartTime(
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (requestedShiftEndTouched) {
      requestedShiftEndErrors = validateShiftEndDate(date, date, station);
      setRequestedShiftEndErrors(requestedShiftEndErrors);
    }

    if (requestedShiftEndHourTouched && requestedShiftEndMinuteTouched) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        date,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (
      requestedShiftStartErrors === "" &&
      requestedShiftStartTimeErrors === "" &&
      requestedShiftEndErrors === "" &&
      requestedShiftEndTimeErrors === ""
    ) {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        date,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftStartHourChange = (event) => {
    setRequestedShiftStartHour(event.target.value);
    setRequestedShiftStartHourTouched(true);
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndTimeErrors = "";
    let requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      false
    );
    setRequestedShiftStartErrors(requestedShiftStartErrors);

    if (requestedShiftStartMinuteTouched && requestedShiftStartErrors === "") {
      const tradeStartEvent = {
        offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
        requestedShiftStart: { date: requestedShiftStart, hour: event.target.value, minute: requestedShiftStartMinute },
      };
      requestedShiftStartTimeErrors = validateShiftStartTime(
        requestedShiftStart,
        event.target.value,
        requestedShiftStartMinute,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (
      requestedShiftStartMinuteTouched &&
      requestedShiftEndHourTouched &&
      requestedShiftEndMinuteTouched &&
      requestedShiftStartErrors === ""
    ) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        event.target.value,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftStartTimeErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        event.target.value,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftStartMinuteChange = (event) => {
    setRequestedShiftStartMinute(event.target.value);
    setRequestedShiftStartMinuteTouched(true);
    let requestedShiftStartTimeErrors = "";
    let requestedShiftEndTimeErrors = "";
    let requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      false
    );
    setRequestedShiftStartErrors(requestedShiftStartErrors);

    if (requestedShiftStartHourTouched && requestedShiftStartErrors === "") {
      const tradeStartEvent = {
        offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
        requestedShiftStart: { date: requestedShiftStart, hour: requestedShiftStartHour, minute: event.target.value },
      };
      requestedShiftStartTimeErrors = validateShiftStartTime(
        requestedShiftStart,
        requestedShiftStartHour,
        event.target.value,
        station,
        tradeStartEvent,
        tradeFormType
      );
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
    }

    if (
      requestedShiftStartHourTouched &&
      requestedShiftEndHourTouched &&
      requestedShiftEndMinuteTouched &&
      requestedShiftStartErrors === ""
    ) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        event.target.value,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftStartTimeErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        event.target.value,
        requestedShiftEnd,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndChange = (date) => {
    setRequestedShiftEnd(date);
    setRequestedShiftEndTouched(true);
    let requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      false
    );
    setRequestedShiftStartErrors(requestedShiftStartErrors);
    let requestedShiftEndErrors = validateShiftEndDate(requestedShiftStart, date, station);
    let requestedShiftEndTimeErrors = "";
    setRequestedShiftEndErrors(requestedShiftEndErrors);

    if (requestedShiftEndHourTouched && requestedShiftEndMinuteTouched && requestedShiftStartErrors === "") {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        date,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndErrors === "" && requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        date,
        requestedShiftEndHour,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndHourChange = (event) => {
    setRequestedShiftEndHour(event.target.value);
    setRequestedShiftEndHourTouched(true);
    let requestedShiftEndTimeErrors = "";
    let requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      false
    );
    setRequestedShiftStartErrors(requestedShiftStartErrors);
    let requestedShiftEndErrors = validateShiftEndDate(requestedShiftStart, requestedShiftEnd, station);
    setRequestedShiftEndErrors(requestedShiftEndErrors);

    if (
      requestedShiftEndMinuteTouched &&
      requestedShiftStartHourTouched &&
      requestedShiftStartMinuteTouched &&
      requestedShiftStartErrors === "" &&
      requestedShiftEndErrors === ""
    ) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        event.target.value,
        requestedShiftEndMinute,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        event.target.value,
        requestedShiftEndMinute,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedShiftEndMinuteChange = (event) => {
    setRequestedShiftEndMinute(event.target.value);
    setRequestedShiftEndMinuteTouched(true);
    let requestedShiftEndTimeErrors = "";
    let requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      false
    );
    setRequestedShiftStartErrors(requestedShiftStartErrors);
    let requestedShiftEndErrors = validateShiftEndDate(requestedShiftStart, requestedShiftEnd, station);
    setRequestedShiftEndErrors(requestedShiftEndErrors);

    if (
      requestedShiftEndHourTouched &&
      requestedShiftStartHourTouched &&
      requestedShiftStartMinuteTouched &&
      requestedShiftStartErrors === "" &&
      requestedShiftEndErrors === ""
    ) {
      requestedShiftEndTimeErrors = validateShiftEndTime(
        requestedShiftStart,
        requestedShiftEnd,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEndHour,
        event.target.value,
        unionContract,
        station
      );
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
    }

    if (requestedShiftEndTimeErrors === "") {
      let duplicateShiftErrors = validateDuplicateShifts(
        offeredShiftStart,
        offeredShiftStartHour,
        offeredShiftStartMinute,
        offeredShiftEnd,
        offeredShiftEndHour,
        offeredShiftEndMinute,
        requestedShiftStart,
        requestedShiftStartHour,
        requestedShiftStartMinute,
        requestedShiftEnd,
        requestedShiftEndHour,
        event.target.value,
        isAdminPage
      );
      setDuplicateShiftErrors(duplicateShiftErrors);
    }
  };

  const handleRequestedReliefShiftSelectChange = (event) => {
    setIsRequestedReliefShift(event.target.value);
    setRequestedReliefShiftErrors(validateReliefShift(event.target.value));
  };

  const handleClickSubmitDialogOpen = () => {
    if (tradeFormType === "Giveaway" && !isGiveawayFormValidationSuccess()) {
      return;
    }
    if (tradeFormType !== "Giveaway" && !isFormValidationSuccess()) {
      return;
    }
    setIsSubmitDialogOpen(true);
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    setIsCancelDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsCancelDialogOpen(false);
  };

  const isFormValidationSuccess = () => {
    const stationErrors = validateStation(station);
    const tradeFormTypeErrors = validateTradeFormType(tradeFormType);
    const shiftWorkgroupErrors = validateShiftWorkgroup(workgroup);
    const duplicateShiftErrors = validateDuplicateShifts(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEnd,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      requestedShiftStart,
      requestedShiftStartHour,
      requestedShiftStartMinute,
      requestedShiftEnd,
      requestedShiftEndHour,
      requestedShiftEndMinute,
      isAdminPage
    );
    const offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      true
    );
    const tradeStartEvent = {
      offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
      requestedShiftStart: {
        date: requestedShiftStart,
        hour: requestedShiftStartHour,
        minute: requestedShiftStartMinute,
      },
    };
    const offeredShiftStartTimeErrors = validateShiftStartTime(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      station,
      tradeStartEvent,
      tradeFormType
    );
    const offeredShiftEndErrors = validateShiftEndDate(offeredShiftStart, offeredShiftEnd, station);
    const offeredShiftEndTimeErrors = validateShiftEndTime(
      offeredShiftStart,
      offeredShiftEnd,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      unionContract,
      station
    );
    const offeredReliefShiftErrors = validateReliefShift(isOfferedReliefShift);
    const requestedEmployeeErrors = validateEmployee(
      requestedEmployeeId,
      requestedEmployeeFirstName,
      requestedEmployeeLastName,
      station,
      requestedEmployeeWorkgroup
    );
    const requestedShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      requestedShiftStart,
      offeredShiftStart,
      station,
      true
    );
    const requestedShiftStartTimeErrors = validateShiftStartTime(
      requestedShiftStart,
      requestedShiftStartHour,
      requestedShiftStartMinute,
      station,
      tradeStartEvent,
      tradeFormType
    );
    const requestedShiftEndErrors = validateShiftEndDate(requestedShiftStart, requestedShiftEnd, station);
    const requestedShiftEndTimeErrors = validateShiftEndTime(
      requestedShiftStart,
      requestedShiftEnd,
      requestedShiftStartHour,
      requestedShiftStartMinute,
      requestedShiftEndHour,
      requestedShiftEndMinute,
      unionContract,
      station
    );
    const requestedReliefShiftErrors = validateReliefShift(isRequestedReliefShift);

    setStationErrors(stationErrors);
    setTradeFormTypeErrors(tradeFormTypeErrors);
    setWorkgroupErrors(shiftWorkgroupErrors);

    if (isTopFormFilledOut()) {
      setOfferedShiftStartErrors(offeredShiftStartErrors);
      setOfferedShiftStartHourTouched(true);
      setOfferedShiftStartMinuteTouched(true);
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
      setOfferedShiftEndTouched(true);
      setOfferedShiftEndHourTouched(true);
      setOfferedShiftEndMinuteTouched(true);
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
      setOfferedReliefShiftErrors(offeredReliefShiftErrors);
      setRequestedEmployeeErrors(requestedEmployeeErrors);
      setRequestedShiftStartErrors(requestedShiftStartErrors);
      setRequestedShiftStartHourTouched(true);
      setRequestedShiftStartMinuteTouched(true);
      setRequestedShiftStartTimeErrors(requestedShiftStartTimeErrors);
      setRequestedShiftEndErrors(requestedShiftEndErrors);
      setRequestedShiftEndTouched(true);
      setRequestedShiftEndHourTouched(true);
      setRequestedShiftEndMinuteTouched(true);
      setRequestedShiftEndTimeErrors(requestedShiftEndTimeErrors);
      setRequestedReliefShiftErrors(requestedReliefShiftErrors);
      if (
        offeredShiftStartErrors === "" &&
        offeredShiftStartTimeErrors === "" &&
        offeredShiftEndErrors === "" &&
        offeredShiftEndTimeErrors === "" &&
        requestedShiftStartErrors === "" &&
        requestedShiftStartTimeErrors === "" &&
        requestedShiftEndErrors === "" &&
        requestedShiftEndTimeErrors === ""
      ) {
        setDuplicateShiftErrors(duplicateShiftErrors);
      }
    }

    return (
      stationErrors === "" &&
      tradeFormTypeErrors === "" &&
      shiftWorkgroupErrors === "" &&
      duplicateShiftErrors === "" &&
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === "" &&
      offeredReliefShiftErrors === "" &&
      requestedEmployeeErrors === "" &&
      requestedShiftStartErrors === "" &&
      requestedShiftStartTimeErrors === "" &&
      requestedShiftEndErrors === "" &&
      requestedShiftEndTimeErrors === "" &&
      requestedReliefShiftErrors === ""
    );
  };

  const isGiveawayFormValidationSuccess = () => {
    const stationErrors = validateStation(station);
    const tradeFormTypeErrors = validateTradeFormType(tradeFormType);
    const shiftWorkgroupErrors = validateShiftWorkgroup(workgroup);
    const offeredShiftStartErrors = validateShiftStartDate(
      tradeFormType,
      offeredShiftStart,
      requestedShiftStart,
      station,
      true
    );
    const tradeStartEvent = {
      offeredShiftStart: { date: offeredShiftStart, hour: offeredShiftStartHour, minute: offeredShiftStartMinute },
      requestedShiftStart: {
        date: requestedShiftStart,
        hour: requestedShiftStartHour,
        minute: requestedShiftStartMinute,
      },
    };
    const offeredShiftStartTimeErrors = validateShiftStartTime(
      offeredShiftStart,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      station,
      tradeStartEvent,
      tradeFormType
    );
    const offeredShiftEndErrors = validateShiftEndDate(offeredShiftStart, offeredShiftEnd, station);
    const offeredShiftEndTimeErrors = validateShiftEndTime(
      offeredShiftStart,
      offeredShiftEnd,
      offeredShiftStartHour,
      offeredShiftStartMinute,
      offeredShiftEndHour,
      offeredShiftEndMinute,
      unionContract,
      station
    );
    const offeredReliefShiftErrors = validateReliefShift(isOfferedReliefShift);
    const requestedEmployeeErrors = validateEmployee(
      requestedEmployeeId,
      requestedEmployeeFirstName,
      requestedEmployeeLastName,
      station,
      requestedEmployeeWorkgroup
    );

    setStationErrors(stationErrors);
    setTradeFormTypeErrors(tradeFormTypeErrors);
    setWorkgroupErrors(shiftWorkgroupErrors);

    if (isTopFormFilledOut()) {
      setOfferedShiftStartErrors(offeredShiftStartErrors);
      setOfferedShiftStartHourTouched(true);
      setOfferedShiftStartMinuteTouched(true);
      setOfferedShiftStartTimeErrors(offeredShiftStartTimeErrors);
      setOfferedShiftEndErrors(offeredShiftEndErrors);
      setOfferedShiftEndTouched(true);
      setOfferedShiftEndHourTouched(true);
      setOfferedShiftEndMinuteTouched(true);
      setOfferedShiftEndTimeErrors(offeredShiftEndTimeErrors);
      setOfferedReliefShiftErrors(offeredReliefShiftErrors);
      setRequestedEmployeeErrors(requestedEmployeeErrors);
    }

    return (
      stationErrors === "" &&
      tradeFormTypeErrors === "" &&
      shiftWorkgroupErrors === "" &&
      offeredShiftStartErrors === "" &&
      offeredShiftStartTimeErrors === "" &&
      offeredShiftEndErrors === "" &&
      offeredShiftEndTimeErrors === "" &&
      offeredReliefShiftErrors === "" &&
      requestedEmployeeErrors === ""
    );
  };

  const getReferenceNumberForCreatedTrade = () => {
    return createdTrade[0].referenceNumber;
  };

  const isTopFormFilledOut = () => {
    return station !== "" && tradeFormType !== "" && workgroup !== "";
  };
  //   ShiftTradeForm State Handlers - END
  const renderTradeForm = (tradeFormTypeName) => {
    switch (tradeFormTypeName) {
      case "Shift Trade":
        return (
          <ShiftTradeForm
            isAdminCreateTrade={false}
            station={stationFilter}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            offeredEmployeeId={offeredEmployeeId}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeErrors={requestedEmployeeErrors}
            requestedEmployeeAutocompleteKey={requestedEmployeeAutocompleteKey}
            requestedShiftStart={requestedShiftStart}
            requestedShiftStartErrors={requestedShiftStartErrors}
            requestedShiftStartHour={requestedShiftStartHour}
            requestedShiftStartMinute={requestedShiftStartMinute}
            requestedShiftStartTimeErrors={requestedShiftStartTimeErrors}
            requestedShiftEnd={requestedShiftEnd}
            requestedShiftEndErrors={requestedShiftEndErrors}
            requestedShiftEndHour={requestedShiftEndHour}
            requestedShiftEndMinute={requestedShiftEndMinute}
            requestedShiftEndTimeErrors={requestedShiftEndTimeErrors}
            isRequestedReliefShift={isRequestedReliefShift}
            requestedReliefShiftErrors={requestedReliefShiftErrors}
            onEmpOneChange={handleEmpOneSelectChange}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoChange={handleEmpTwoSelectChange}
            onRequestedShiftStartChange={handleRequestedShiftStartChange}
            onRequestedShiftStartHourChange={handleRequestedShiftStartHourChange}
            onRequestedShiftStartMinuteChange={handleRequestedShiftStartMinuteChange}
            onRequestedShiftEndChange={handleRequestedShiftEndChange}
            onRequestedShiftEndHourChange={handleRequestedShiftEndHourChange}
            onRequestedShiftEndMinuteChange={handleRequestedShiftEndMinuteChange}
            onRequestedReliefShiftSelectChange={handleRequestedReliefShiftSelectChange}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
            isRequestedShiftDSTWarningOpen={isRequestedShiftDSTWarningOpen}
          />
        );
      case "Time Trade":
        return (
          <TimeTradeForm
            isAdminCreateTrade={false}
            station={stationFilter}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            workgroup={workgroup}
            unionContract={unionContract}
            offeredEmployeeId={offeredEmployeeId}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeErrors={requestedEmployeeErrors}
            requestedEmployeeAutocompleteKey={requestedEmployeeAutocompleteKey}
            requestedShiftStart={requestedShiftStart}
            requestedShiftStartErrors={requestedShiftStartErrors}
            requestedShiftStartHour={requestedShiftStartHour}
            requestedShiftStartMinute={requestedShiftStartMinute}
            requestedShiftStartTimeErrors={requestedShiftStartTimeErrors}
            requestedShiftEnd={requestedShiftEnd}
            requestedShiftEndErrors={requestedShiftEndErrors}
            requestedShiftEndHour={requestedShiftEndHour}
            requestedShiftEndMinute={requestedShiftEndMinute}
            requestedShiftEndTimeErrors={requestedShiftEndTimeErrors}
            isRequestedReliefShift={isRequestedReliefShift}
            requestedReliefShiftErrors={requestedReliefShiftErrors}
            onEmpOneChange={handleEmpOneSelectChange}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoChange={handleEmpTwoSelectChange}
            onRequestedShiftStartChange={handleRequestedShiftStartChange}
            onRequestedShiftStartHourChange={handleRequestedShiftStartHourChange}
            onRequestedShiftStartMinuteChange={handleRequestedShiftStartMinuteChange}
            onRequestedShiftEndChange={handleRequestedShiftEndChange}
            onRequestedShiftEndHourChange={handleRequestedShiftEndHourChange}
            onRequestedShiftEndMinuteChange={handleRequestedShiftEndMinuteChange}
            onRequestedReliefShiftSelectChange={handleRequestedReliefShiftSelectChange}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
            isRequestedShiftDSTWarningOpen={isRequestedShiftDSTWarningOpen}
          />
        );
      case "Giveaway":
        return (
          <GiveawayRequestForm
            isAdminCreateTrade={false}
            station={stationFilter}
            jobCode={userSession.jobCode} // TODO: We shouldn't need this later, backend needs to be able to take workgroup instead
            offeredEmployeeId={offeredEmployeeId}
            offeredShiftStart={offeredShiftStart}
            offeredShiftStartErrors={offeredShiftStartErrors}
            offeredShiftStartHour={offeredShiftStartHour}
            offeredShiftStartMinute={offeredShiftStartMinute}
            offeredShiftStartTimeErrors={offeredShiftStartTimeErrors}
            offeredShiftEnd={offeredShiftEnd}
            offeredShiftEndErrors={offeredShiftEndErrors}
            offeredShiftEndHour={offeredShiftEndHour}
            offeredShiftEndMinute={offeredShiftEndMinute}
            offeredShiftEndTimeErrors={offeredShiftEndTimeErrors}
            isOfferedReliefShift={isOfferedReliefShift}
            offeredReliefShiftErrors={offeredReliefShiftErrors}
            requestedEmployeeId={requestedEmployeeId}
            requestedEmployeeErrors={requestedEmployeeErrors}
            requestedEmployeeAutocompleteKey={requestedEmployeeAutocompleteKey}
            onEmpOneChange={handleEmpOneSelectChange}
            onOfferedShiftStartChange={handleOfferedShiftStartChange}
            onOfferedShiftStartHourChange={handleOfferedShiftStartHourChange}
            onOfferedShiftStartMinuteChange={handleOfferedShiftStartMinuteChange}
            onOfferedShiftEndChange={handleOfferedShiftEndChange}
            onOfferedShiftEndHourChange={handleOfferedShiftEndHourChange}
            onOfferedShiftEndMinuteChange={handleOfferedShiftEndMinuteChange}
            onOfferedReliefShiftSelectChange={handleOfferedReliefShiftSelectChange}
            onEmpTwoChange={handleEmpTwoSelectChange}
            isOfferedShiftDSTWarningOpen={isOfferedShiftDSTWarningOpen}
          />
        );
      default:
        // TODO: What do you want this to be?
        console.log("Default?");
        return "";
    }
  };

  const renderTradeFormTypeMenuItem = () => {
    const tradeTypes = ["Giveaway", "Shift Trade", "Time Trade"];
    return tradeTypes
      .slice()
      .sort()
      .map((tradeType) => {
        return (
          <MenuItem key={tradeType} value={tradeType}>
            {tradeType}
          </MenuItem>
        );
      });
  };

  const renderSubmitAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        isDialogOpen={isSubmitDialogOpen}
        message={ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THE_REQUEST_MESSAGE}
        handleNoButtonClicked={handleClickSubmitDialogClose}
        handleYesButtonClicked={async () => await handleSubmitClicked()}
        isYesLoading={isTradeFormSubmissionTriggered && isTradeFormSubmissionLoading}
      />
    );
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        isDialogOpen={isCancelDialogOpen}
        message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)}
        handleNoButtonClicked={handleClickCancelDialogClose}
        redirectTo={props.featureConfig.path}
      />
    );
  };
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_TRADES, BREADCRUMBS_TITLE.REQUEST_TRADE];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.path];
  const confirmationRequired = () => {
    if (tradeFormType !== "") {
      return true;
    } else {
      return false;
    }
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents enable-scroll" data-testid="employee-request-trade-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {isPageLoading ? (
            <CircularProgress />
          ) : (
            <>
              {props.isDesktopView ? (
                <StarsBreadcrumbs
                  breadcrumbsList={breadcrumbsList}
                  breadcrumbsLinks={breadcrumbsLinks}
                  isConfirmationRequired={confirmationRequired()}
                  cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
                />
              ) : (
                ""
              )}
              <div className="trade-form-page-main">
                <Grid item xs={12}>
                  <Typography component={"div"}>
                    <Box className="page-title-left">{REQUEST_TRADE_TITLE}</Box>
                  </Typography>
                </Grid>

                <Divider className="divider" />

                <Grid container>
                  <Grid item xs={12}>
                    <Box mt={1} mb={1}>
                      {stationList.length > 1 ? (
                        <StarsSelectInput
                          label="Station"
                          menuMessage="Station"
                          value={stationFilter}
                          allOptions={stationList}
                          sortComparator={sortOnCaseSensitiveTexts}
                          onChange={handleStationChange}
                          onBlur={handleStationBlur}
                          errors={stationErrors}
                          className="select-field"
                          multiple={false}
                        />
                      ) : (
                        <TextField
                          className="text-field"
                          label="Station"
                          variant="outlined"
                          value={station}
                          onChange={handleStationChange}
                          onBlur={handleStationBlur}
                          disabled
                          error={stationErrors !== ""}
                          helperText={stationErrors}
                        />
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box mt={1} mb={1}>
                      {workgroupErrors !== "" ? (
                        <TextField
                          error
                          className="text-field"
                          label="Shift Workgroup"
                          variant="outlined"
                          value={workgroup}
                          helperText={workgroupErrors}
                        />
                      ) : (
                        <TextField
                          className="text-field"
                          label="Shift Workgroup"
                          variant="outlined"
                          value={workgroup}
                          disabled
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Grid container>
                  <Grid item xs={12}>
                    <Box mt={1} mb={1}>
                      {tradeFormTypeErrors !== "" ? (
                        <FormControl variant="outlined">
                          <InputLabel>Trade Type</InputLabel>
                          <Select
                            id="tradeFormTypeSelectElement"
                            className="select-field"
                            value={tradeFormType}
                            onChange={handleTradeFormTypeSelectChange}
                            onBlur={handleTradeFormTypeSelectBlur}
                            label="Trade Type"
                            error
                          >
                            <MenuItem value="" disabled>
                              <em>Trade Type</em>
                            </MenuItem>
                            {renderTradeFormTypeMenuItem()}
                          </Select>
                          <FormHelperText error>{tradeFormTypeErrors}</FormHelperText>
                        </FormControl>
                      ) : (
                        <FormControl variant="outlined">
                          <InputLabel>Trade Type</InputLabel>
                          <Select
                            id="tradeFormTypeSelectElement"
                            className="select-field"
                            value={tradeFormType}
                            onChange={handleTradeFormTypeSelectChange}
                            onBlur={handleTradeFormTypeSelectBlur}
                            label="Trade Type"
                          >
                            <MenuItem value="" disabled>
                              <em>Trade Type</em>
                            </MenuItem>
                            {renderTradeFormTypeMenuItem()}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {isTopFormFilledOut() ? renderTradeForm(tradeFormType) : ""}

                {isTradeFormSubmissionError ? (
                  <Alert severity="error" className="text-field">
                    <AlertTitle>{tradeFormSubmissionErrors}</AlertTitle>
                  </Alert>
                ) : (
                  ""
                )}

                <Box m={1}>
                  <Button
                    className="button"
                    variant="outlined"
                    color="primary"
                    size="large"
                    fullWidth={true}
                    onClick={handleClickCancelDialogOpen}
                  >
                    <Typography variant="h5" component="h2">
                      <Box className="order-selection-text">Cancel</Box>
                    </Typography>
                  </Button>
                </Box>

                <Box m={1}>
                  <div className="link" style={{ width: "100%" }}>
                    {isTradeFormSubmissionTriggered && isTradeFormSubmissionLoading ? (
                      <Button
                        className="button"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth={true}
                        onClick={handleClickSubmitDialogOpen}
                        disabled
                      >
                        <CircularProgress size={24} />
                      </Button>
                    ) : (
                      <Button
                        className="button"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth={true}
                        onClick={handleClickSubmitDialogOpen}
                      >
                        <Typography variant="h5" component="h2">
                          <Box className="order-selection-text">Submit</Box>
                        </Typography>
                      </Button>
                    )}
                  </div>
                </Box>
              </div>
            </>
          )}
          {renderSubmitAreYouSureDialog()}
          {renderCancelAreYouSureDialog()}
          {isTradeSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isTradeSuccessDialogOpen}
              title={TRADE_CONFIRMATION_TITLE}
              subTitle={REFERENCE_NUMBER_SUB_TITLE(getReferenceNumberForCreatedTrade())}
              message={TRADE_OFFERED_MESSAGE}
              redirectTo={props.featureConfig.path}
            />
          )}
          {isTradeEventEligibilityWarningDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isTradeEventEligibilityWarningDialogOpen}
              title={TRADE_ELIGIBILITY_TITLE}
              hasError={true}
              error={tradeEligibilityErrorMessage.current}
              handleOKClick={() => setIsTradeEventEligibilityWarningDialogOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeRequestTradePage;
